import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Guid } from 'guid-typescript';
import { startCase, toLower } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { AwvDefaultConfigurationService } from 'src/app/admin/dashboards/awv-default-configuration/services/awv-default-configuration.service';
import { Client } from 'src/app/models/client.model';
import { CoverageTypes, Patient, Provider } from 'src/app/models/patient.model';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { DataService } from 'src/app/services/data.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { PatientService } from 'src/app/services/patient.service';
import { ProgramService } from 'src/app/services/programs/program.service';
import { MrnValidatorService } from 'src/app/shared/validators/mrn.validator';
import { ageValidator } from 'src/app/shared/validators/patient-age.validators';
import { apcmValidator, programsValidator } from 'src/app/shared/validators/programs.validators';
import { environment } from 'src/environments/environment';
import { AddProvidersComponent } from '../add-providers/add-providers.component';
import { InitialDataModel } from './../../../services/models/initial-data-model.service';
import { SnackService } from './../../../services/snack.service';

@Component({
  selector: 'app-patient-demographics-dialog',
  templateUrl: './patient-demographics-dialog.component.html',
  styleUrls: ['./patient-demographics-dialog.component.scss'],
})
export class PatientDemographicsDialogComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper: MatStepper;

  // THIS IS THE NEW MINIMIZED PATIENT LOADING FORM.
  patientForm: UntypedFormGroup;
  serverMessage: '';
  owners = [];
  isLoading = false;
  clients$: Observable<Client[]>;
  timezoneConfig = {
    hideSelected: false,
    dropdownPosition: 'top',
    appearance: 'underline',
    clearOnBackspace: true,
    closeOnSelect: true,
    appendTo: null,
  };
  languageModel = { label: 'Language', showLabel: true, appearance: 'legacy', showOnlyActive: false };
  showSecondaryProviderPanel = false;
  providers: Provider[] = [];
  programs$: Observable<any[]>;
  filteredProgramOptions$: Observable<any[]>;
  availablePrograms: any[] = [];
  unsubscribe = new Subject<void>();
  prograsmValidator: boolean;
  apcmTiers: { name: string; value: string }[] = [];
  isEnableAwv = false;

  // Step validity cache to avoid frequent recalculations
  stepValidityCache: { [key: number]: boolean } = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PatientDemographicsDialogComponent>,
    private dataService: DataService,
    public dataModel: InitialDataModel,
    public ds: DataService,
    private auth: NewAuthService,
    private afAuth: AngularFireAuth,
    private snack: SnackService,
    private patientService: PatientService,
    private mrnValidator: MrnValidatorService,
    private router: Router,
    private fsService: FirestoreService,
    private clientService: ClientServicesService,
    private dialog: MatDialog,
    private programService: ProgramService,
    private awvConfiguration: AwvDefaultConfigurationService
  ) {}

  ngOnInit(): void {
    this.clients$ = this.dataModel.clients$;
    this.initializeForm();
    this.apcmTiers = this.dataService.apcmTiers;
    this.setupFormChangeListeners();
  }

  enableAwvForm(clientId: string): void {
    this.awvConfiguration
      .isEnableAwvForm(clientId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((isEnable) => {
        this.isEnableAwv = isEnable;
        this.getPrograms();
      });
  }

  getPrograms(): void {
    this.programs$ = this.programService.getPrograms();
    this.programs$.pipe(take(1), takeUntil(this.unsubscribe)).subscribe((programs) => {
      this.availablePrograms = programs.filter((p) => this.isEnableAwv === true || p.value !== 'awv');
      this.availablePrograms.forEach((programObj) => {
        (this.patientForm.get('status') as UntypedFormGroup).addControl(programObj.value, new UntypedFormControl(false, []));
      });
      this.setFilteredProgramOption();
    });
  }

  get programs(): any[] {
    return (this.patientForm.get('status') as UntypedFormGroup)?.getRawValue()
      ? Object.keys((this.patientForm.get('status') as UntypedFormGroup)?.getRawValue())
          .filter(
            (key) =>
              this.availablePrograms.some((program) => program.value === key) &&
              this.patientForm.get('status').get(key).value
          )
          .map((key) =>
            this.availablePrograms.find((program) => {
              if (key.startsWith('rpm')) {
                return program.value === this.patientForm.get('rpm_category').value;
              }
              return program.value === key;
            })
          )
      : [];
  }

  /**
   * Checks if a specific step is valid based on its form controls
   * Uses caching to avoid redundant calculations
   *
   * @param stepIndex The index of the step to validate
   * @returns boolean indicating if the step is valid
   */
  isStepValid(stepIndex: number): boolean {
    // Return cached value if available and form hasn't changed since last check
    if (this.stepValidityCache[stepIndex] !== undefined) {
      return this.stepValidityCache[stepIndex];
    }

    // Calculate validity based on step index
    let isValid: boolean;

    switch (stepIndex) {
      case 0: // Client & Basic Info
        isValid =
          this.patientForm.get('client')?.valid &&
          this.patientForm.get('firstName')?.valid &&
          this.patientForm.get('lastName')?.valid &&
          this.patientForm.get('dob')?.valid &&
          this.patientForm.get('emr_id')?.valid;
        break;
      case 1: // Providers & Timezone
        isValid = this.patientForm.get('timezone')?.valid && this.patientForm.get('provider')?.valid;
        break;
      case 2: // Programs & Notes
        isValid =
          !this.patientForm.get('status').errors?.noPrograms && !this.patientForm.get('status').errors?.apcmConflict;
        if (this.patientForm.get('status').get('apcm')?.value) {
          isValid = isValid && this.patientForm.get('status')?.get('tier_apcm')?.valid;
        }
        break;
      case 3: // Consents
        const consentsKeys = Object.keys(this.patientForm.get('status').value).filter((key) =>
          key.startsWith('consent_')
        );
        isValid = consentsKeys.every((key) => this.patientForm.get('status').get(key).value);
        break;
      default:
        isValid = true;
        break;
    }

    // Cache the result
    this.stepValidityCache[stepIndex] = isValid;
    return isValid;
  }

  /**
   * Navigates to a specific step in the stepper
   *
   * @param stepIndex The index of the step to navigate to
   */
  goToStep(stepIndex: number): void {
    if (this.stepper) {
      this.stepper.selectedIndex = stepIndex;
    }
  }

  async loadPatientToDatabase() {
    this.isLoading = true;
    const patient = this.buildPatient();
    const canSavePatient = await this.canSavePatientAfterBillingRestriction();
    if (canSavePatient) {
      this.savePatient(patient);
    } else {
      this.showErrorNotification();
    }
  }

  async afterSaveActions(patient: Patient, response: any) {
    console.log('POST call successful value returned in body', response);
    await this.afAuth.sendPasswordResetEmail(patient.email);
    this.snack.genericSnackBar(response.message, ['success-snackbar']);
    const id = response?.user_id;
    this.dialogRef.close();
    this.isLoading = false;
    await this.updateClientInfo(patient.client_responsible_id);
    if (this.data?.userProspect?.id) {
      await this.fsService.update(`users_prospective/${this.data.userProspect.id}`, { status: 'Enrolled' });
    }
    await this.saveFirstClinicalResolution({ ...patient, user_id: id });
    await this.router.navigateByUrl(`clinical/patient-detail/${id}`);
  }

  compareClientObjects(object1: any, object2: any) {
    return object1 && object2 && object1.client_id === object2.client_id;
  }

  setMaxDate(): Date {
    return new Date();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  openProviderPanel(): void {
    const dialogRef = this.dialog.open(AddProvidersComponent, {
      width: '650px',
      data: { providers: this.providers },
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.providers = result;
      this.patientForm.get('provider').setValue(result.find((provider) => provider.rank === 1));
    });
  }

  setFilteredProgramOption(): void {
    this.filteredProgramOptions$ = this.patientForm.get('programInput').valueChanges.pipe(
      filter((data) => data && data.length > 0),
      map((data) =>
        this.availablePrograms.filter(
          (program) =>
            program.name.toLowerCase().includes(data.toLowerCase()) ||
            program.value.toLowerCase().includes(data.toLowerCase())
        )
      )
    );
  }

  selectProgram(program: string): void {
    (this.patientForm.get('status') as UntypedFormGroup).get(program).setValue(true);
    (this.patientForm.get('status') as UntypedFormGroup).addControl(
      `consent_${program}`,
      new UntypedFormControl(false, [Validators.required])
    );
    console.log('status form', this.patientForm.get('status').value);
    if (program === 'apcm') {
      this.addTierApcm();
    }
    this.patientForm.get('programInput').reset();
    if (program.startsWith('rpm')) {
      this.patientForm.get('rpm_category').setValue(program);
    }
  }

  addTierApcm(): void {
    (this.patientForm.get('status') as UntypedFormGroup).addControl('tier_apcm', new UntypedFormControl('', []));
    this.patientForm.get('status').get('tier_apcm').setValidators([Validators.required]);
    this.patientForm.get('status').get('tier_apcm').updateValueAndValidity();
  }

  removeProgram(program: string): void {
    (this.patientForm.get('status') as UntypedFormGroup).get(program).setValue(false);
    (this.patientForm.get('status') as UntypedFormGroup).removeControl(`consent_${program}`);
    if (program === 'apcm') {
      (this.patientForm.get('status') as UntypedFormGroup).removeControl('tier_apcm');
    }
    this.patientForm.get('status').get('tier_apcm').setValidators([]);
    this.patientForm.get('status').get('tier_apcm').updateValueAndValidity();
  }

  onNextFromBasicInfo(stepper: MatStepper): void {
    this.patientForm.markAllAsTouched();
    const emrControl = this.patientForm.get('emr_id');
    emrControl?.markAsDirty();
    this.stepValidityCache[0] = undefined;
    if (this.isStepValid(0)) {
      stepper.steps.toArray()[0].completed = true;
      stepper.next();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Sets up listeners to invalidate the step validity cache when form values change
   */
  private setupFormChangeListeners(): void {
    // Listen for form value changes and invalidate the cache
    this.patientForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      // Reset the cache when form values change
      this.stepValidityCache = {};
    });

    // Also invalidate cache when specific form controls change
    const criticalControls = ['client', 'firstName', 'lastName', 'dob', 'emr_id', 'timezone', 'status'];
    criticalControls.forEach((controlName) => {
      const control = this.patientForm.get(controlName);
      if (control) {
        control.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
          // Only invalidate the relevant step's cache
          if (controlName === 'timezone') {
            delete this.stepValidityCache[1];
          } else if (controlName === 'status') {
            delete this.stepValidityCache[2];
            delete this.stepValidityCache[3];
          } else {
            delete this.stepValidityCache[0];
          }
        });
      }
    });
  }

  private initializeForm(): void {
    this.patientForm = this.fb.group({
      client: [this.data.userProspect?.client ?? '', [Validators.required]],
      firstName: [this.data.userProspect?.first_name ?? '', [Validators.required]],
      middleName: [this.data.userProspect?.midlle_name ?? ''],
      lastName: [this.data.userProspect?.last_name ?? '', [Validators.required]],
      dob: [
        this.data.userProspect?.dob ? new Date(this.data.userProspect?.dob) : '',
        [Validators.required, ageValidator],
      ],
      email: [this.data.userProspect?.email ?? '', [Validators.email]],
      language: ['English'],
      provider: ['', [Validators.required]],
      emr_id: new UntypedFormControl(this.data.userProspect?.emr_id ?? '', {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      ssn_id: ['', [Validators.minLength(4), Validators.maxLength(4)]],
      notes: [this.data.userProspect?.notes ?? '', []],
      programInput: [''],
      rpm_category: [''],
      status: this.fb.group({ rpm: [false] }, { validators: [programsValidator, apcmValidator] }),
      timezone: ['', Validators.required],
      alert_sms: ['', [Validators.minLength(10), Validators.pattern('[0-9]*')]],
      hubspot_id: [''],
    });
    this.clients$.subscribe((clients) => {
      const clientFromProspect = clients.find((client) => client.client_id === this.data.userProspect?.client_id);
      if (!this.patientForm.get('client').value) {
        this.patientForm.get('client').setValue(clientFromProspect);
      }
    });
    this.setMrnAsyncValidator(this.patientForm.get('client')?.value?.client_id);

    this.patientForm.get('client')?.valueChanges.subscribe((client: Client) => {
      this.setMrnAsyncValidator(client?.client_id || client?.client_ids?.uid);
      this.enableAwvForm(client?.client_id || client?.client_ids?.uid);
    });
  }

  private setMrnAsyncValidator(clientId: string): void {
    const emrCtrl = this.patientForm.get('emr_id');
    if (!emrCtrl) {
      return;
    }

    emrCtrl.setAsyncValidators([this.mrnValidator.createMrnAsyncValidator(null, clientId)]);

    emrCtrl.updateValueAndValidity();
  }

  private buildPatient(): Patient {
    const uid = Guid.create().toString();
    const userEmail = this.patientForm.value['email'] ? this.patientForm.value['email'] : `${uid}@getwelby.com`;
    const firstName = startCase(toLower(this.patientForm.value.firstName?.trim()));
    const lastName = startCase(toLower(this.patientForm.value.lastName?.trim()));
    const middleName = startCase(toLower(this.patientForm.value.middleName?.trim()));
    const alertLine = this.ds.returnProviderTwilioLine(this.patientForm.value.provider?.uid);
    const formStatus = { ...this.patientForm.value.status };
    if (formStatus.apcm === true) {
      formStatus.apcm_last_update = new Date();
    }

    return {
      user_account_access: [this.auth.user.user_id],
      client_accounts: [this.auth.user.client_responsible_id],
      alert_contacts: [{ type: 'provider', data: alertLine }],
      client_responsible:
        this.dataModel.clients[this.patientForm.value.client?.client_id ?? this.patientForm.value.client?.id]
          ?.client_name ?? '',
      client_responsible_id: this.patientForm.value?.client?.client_id ?? this.patientForm.value.client?.id,
      provider_id: this.patientForm.value.provider?.uid,
      providers: this.providers,
      dob: new Date(this.patientForm.value.dob),
      email: userEmail,
      emr_id: this.patientForm.value.emr_id,
      hubspot_id: this.patientForm.value.hubspot_id,
      firstName,
      middleName,
      gender: 'other',
      height_inches: 0,
      lastName,
      language: this.patientForm.value.language,
      notes: this.patientForm.value.notes,
      pregnancies: 0,
      search_terms: [
        firstName.toLowerCase(),
        lastName.toLowerCase(),
        userEmail.toLowerCase(),
        this.dataService.getFirstName(this.patientForm.value.provider?.uid).toLowerCase(),
        this.dataService.getLastName(this.patientForm.value.provider?.uid).toLowerCase(),
      ],
      status: formStatus,
      source: {
        manual: true,
        sheet: 'N/A',
        tab: 'N/A',
        user: this.auth.user.email,
      },
      ssn_id: this.patientForm.value.ssn_id,
      weight_lbs: 0,
      providerGenerated: true,
      timezone: this.patientForm.get('timezone').value,
      primary_coverage_type: CoverageTypes.OTHER,
    };
  }

  private async canSavePatientAfterBillingRestriction(): Promise<boolean> {
    const currentUser = this.auth.user;
    const currentClient = (await this.clientService.getClientById(currentUser.client_responsible_id)).data() as any;
    if (currentClient?.billing_approved) {
      return true;
    } else {
      return currentClient?.patients_loaded >= 0
        ? currentClient.patients_loaded < environment.trial_patient_loads
        : true;
    }
  }

  private async updateClientInfo(clientId?: string) {
    const currentClientId = clientId ?? this.auth.user?.client_responsible_id;
    const currentClient = (await this.clientService.getClientById(currentClientId)).data() as any;
    if (currentClient.patients_loaded >= 0) {
      this.clientService.updateClient(currentClientId, {
        client_name: currentClient.client_name,
        patients_loaded: currentClient.patients_loaded + 1,
      });
    } else {
      this.clientService.updateClient(currentClientId, { client_name: currentClient.client_name, patients_loaded: 1 });
    }
  }

  private showErrorNotification(): void {
    this.snack.genericSnackBar(
      'Error: Exceed the number of patients. Contact administrator.',
      ['error-snackbar'],
      5000
    );
    this.isLoading = false;
    this.dialogRef.close();
  }

  private savePatient(patient: Patient): void {
    this.patientService.createPatient(patient).subscribe(
      (val) => {
        this.afterSaveActions(patient, val);
      },
      (error) => {
        console.error('POST really screwed something up', error);
        this.snack.genericSnackBar(error?.error?.message, ['error-snackbar'], 5000);
        this.dialogRef.close();
        this.isLoading = false;
      },
      () => {
        console.log('The POST observable is now completed.');
      }
    );
  }

  private async saveFirstClinicalResolution(patient: Patient): Promise<void> {
    try {
      const clinicalResolution = {
        assessor: this.auth.user.user_id,
        content: this.buildClinicalResolution(patient),
        follow_up: 'Initial Patient Onboarding',
        associations: {
          uid: patient.user_id,
          client_id: this.auth.user.client_responsible_id,
        },
        category: '',
        date: new Date(),
      };
      await this.patientService.addClinicalResolution(clinicalResolution).pipe(take(1)).toPromise();
    } catch (error) {
      console.error(error);
      this.snack.genericSnackBar('Error: Unable to save clinical resolution', ['error-snackbar'], 5000);
    }
  }

  private buildClinicalResolution(patient: Patient) {
    let clinicalResolution = `<h2>Consent</h2>`;
    if (patient?.status) {
      clinicalResolution += `<p>Patient agreed to informed consent of the following programs:</p><ul>`;
      Object.entries(patient.status).forEach(([key, value]) => {
        if (value && !key?.startsWith('consent') && !key?.startsWith('tier')) {
          clinicalResolution += `<li>${key.toUpperCase()}</li>`;
        }
      });
      clinicalResolution += `<p>Informed patient of the availability of various Welby services, possible cost sharing responsibilities,`;
      clinicalResolution += ` that only one practitioner can furnish and bill services during a calendar month,`;
      clinicalResolution += ` and their right stop services at any time</p>`;
    }
    return clinicalResolution;
  }
}
